import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { EFFECT_LABEL } from "@/lib/agent-permissions-model";
import type { PermissionEffect } from "@/lib/api/agent-permissions";
import type { ActionSchemaEntry } from "@/lib/connectors/schema-sections";
import { cn, FILLED_BOX } from "@/lib/utils";

/** Display the server decision without deriving policy from connector metadata. */
export function PolicyStatus({ effect }: { effect?: PermissionEffect }) {
	return (
		<span className="text-xs text-muted-foreground">
			{effect ? EFFECT_LABEL[effect] : "Policy unavailable"}
		</span>
	);
}

export function PolicySettingsLink({
	ownerSlug,
	connectorKey,
	connectionId,
}: {
	ownerSlug?: string;
	connectorKey?: string;
	connectionId?: number;
}) {
	if (!ownerSlug) return null;
	return (
		<Link
			to="/$owner/settings/organization"
			params={{ owner: ownerSlug }}
			search={{ connector: connectorKey, connection: connectionId }}
			hash="policies"
			className="text-xs underline underline-offset-2"
		>
			Policies
		</Link>
	);
}

// ============================================================
// Shared capability row for previews and controls in the connection form.
// ============================================================

function CapabilityRow({
	name,
	description,
	onInspect,
	leading,
	trailing,
	badge,
}: {
	name: string;
	description?: string;
	/** When set, the name renders as a button that opens the schema peek. */
	onInspect?: () => void;
	/** Optional control before the text (e.g. a feed enable checkbox). */
	leading?: ReactNode;
	/** Optional control after the text. */
	trailing?: ReactNode;
	/** Optional badge beside the name. */
	badge?: ReactNode;
}) {
	return (
		<div className={cn(FILLED_BOX, "flex items-start gap-3 p-3")}>
			{leading}
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-2">
					{onInspect ? (
						<button
							type="button"
							onClick={onInspect}
							className="text-sm font-medium text-left hover:underline"
						>
							{name}
						</button>
					) : (
						<span className="text-sm font-medium">{name}</span>
					)}
					{badge}
				</div>
				{description && (
					<p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
				)}
			</div>
			{trailing}
		</div>
	);
}

// ============================================================
// Feeds Panel
// ============================================================

interface FeedEntry {
	name?: string;
	description?: string;
}

export function FeedsPanel({
	feedsSchema,
	enabledFeeds,
	onToggleFeed,
	onInspect,
}: {
	feedsSchema: Record<string, unknown> | null;
	enabledFeeds?: Set<string>;
	onToggleFeed?: (key: string, enabled: boolean) => void;
	/** When set, rows open the feed's schema peek. */
	onInspect?: (key: string, feed: FeedEntry) => void;
}) {
	if (!feedsSchema) return null;
	const entries = Object.entries(feedsSchema as Record<string, FeedEntry>);
	if (entries.length === 0) return null;

	const interactive = !!enabledFeeds && !!onToggleFeed;

	return (
		<div className="space-y-2">
			<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
				Feeds
			</p>
			<div className="space-y-1.5">
				{entries.map(([key, feed]) => (
					<CapabilityRow
						key={key}
						name={feed.name ?? key}
						description={feed.description}
						onInspect={onInspect ? () => onInspect(key, feed) : undefined}
						leading={
							interactive ? (
								<input
									type="checkbox"
									checked={enabledFeeds.has(key)}
									onChange={(e) => onToggleFeed(key, e.target.checked)}
									className="rounded border-input mt-0.5"
									aria-label={`Enable ${feed.name ?? key}`}
								/>
							) : undefined
						}
					/>
				))}
			</div>
		</div>
	);
}

// ============================================================
// Static actions list from actions_schema (for uninstalled connector preview)
// ============================================================

export function ActionsSchemaPanel({
	actionsSchema,
	onInspect,
	ownerSlug,
	connectorKey,
}: {
	actionsSchema: Record<string, unknown> | null;
	ownerSlug?: string;
	connectorKey?: string;
	/** When set, each action's name renders as a button that opens its schema peek. */
	onInspect?: (key: string, action: ActionSchemaEntry) => void;
}) {
	if (!actionsSchema) return null;
	const entries = Object.entries(
		actionsSchema as Record<string, { name?: string; description?: string }>,
	);
	if (entries.length === 0) return null;

	return (
		<div className="space-y-2">
			<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
				Actions
			</p>
			<div className="space-y-1.5">
				{entries.map(([key, action]) => (
					<CapabilityRow
						key={key}
						name={action.name ?? key}
						description={action.description}
						onInspect={onInspect ? () => onInspect(key, action) : undefined}
						trailing={
							<PolicySettingsLink
								ownerSlug={ownerSlug}
								connectorKey={connectorKey}
							/>
						}
						badge={
							<span className="text-xs text-muted-foreground">
								Policy depends on connection
							</span>
						}
					/>
				))}
			</div>
		</div>
	);
}
